import React, { useCallback } from 'react';
import { Button, Icon, Typography } from '@/components/common';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
	actionRequiredService,
	auditService,
	GET_CASE_STEPS_STATUS,
	GET_DOCUMENTS_CASE,
	GET_LAWYER_CASE_DETAILS,
	GET_LAWYER_SIGN,
} from '@/api';
import { casesLawyerService } from '@/api/casesLawyer';
import { useNotification, usePhaseStatus, useSendFollowUp } from '@/hooks';
import {
	AddFollowUpPayload,
	ApprovePhase,
	CreateDocumentsToSend,
	StepStatusCaseData,
	StepStatusCaseType,
} from '@/interfaces';
import { CaseContext } from '@/context/CaseContext';
import { DocumentCard } from '../DocumentCard';
import Icons from '@/const/icons';
import { RetainerContent } from './RetainerContent';
import { DocumentContent } from './DocumentContent';
import { formatErrorMessage } from '@/lib';
import useModalPreviewPage from '@/hooks/useModalPreviewPage';
import { TemplatePreview } from './TemplatePreview';

interface ItemDocument {
	id: string | undefined;
	title: string;
	badges: string[];
	signatureBadges?: string[];
	onClick?: () => void;
	isCompleted?: boolean;
	isFollowUp?: boolean;
}
interface Props {
	caseId: string | undefined;
	caseSteps?: StepStatusCaseData;
	statusStep: StepStatusCaseType;
}

export const DocumentsToSend: React.FC<Props> = ({
	caseId,
	caseSteps,
	statusStep,
}) => {
	const queryClient = useQueryClient();
	const { toastError, toastSuccess } = useNotification();
	const { ModalPreviewPage, hidePreviewPage, showPreviewPage } =
		useModalPreviewPage();

	const { phaseId, stepId } = usePhaseStatus({
		caseSteps,
		phaseName: 'Applicant Intake Phase',
		stepName: 'Documents to Send',
	});

	const { setSaveStatus } = React.useContext(CaseContext);
	const [isLoadingAutosaved, setIsLoadingAutosaved] = React.useState(false);
	const [listDocuments, setListDocuments] = React.useState<ItemDocument[]>([]);
	const [isModalDocumentOpen, setIsModalDocumentOpen] = React.useState(false);
	const [isModalRetainerOpen, setIsModalRetainerOpen] = React.useState(false);
	const [currentDocumentPreview, setCurrentDocumentPreview] =
		React.useState<CreateDocumentsToSend | null>(null);
	const [documentsPreviewData, setDocumentsPreviewData] = React.useState<
		Partial<CreateDocumentsToSend>[]
	>([]);

	const { data: lawyerSign, refetch } = useQuery({
		queryKey: [GET_LAWYER_SIGN, caseId],
		queryFn: () => casesLawyerService.getLawyerSign(caseId!),
		enabled: !!caseId,
	});

	const { data: documents, refetch: refetchDocuments } = useQuery({
		queryKey: [GET_DOCUMENTS_CASE, caseId],
		queryFn: () => casesLawyerService.getDocuments(caseId!),
	});

	const {
		onOpenSendFollowUp,
		hideModalSendFollowUp,
		renderSendFollowUpModals,
	} = useSendFollowUp({
		isSendFromAdministration: true,
		onSubmit: (data) => onSubmitSendFollowUp(data),
	});

	const onSubmitSendFollowUp = useCallback(
		async (data: AddFollowUpPayload) => {
			try {
				const finalData: AddFollowUpPayload = {
					case_id: caseId || '',
					description: data.description,
					name: data.name,
					email_text: data.email_text,
					follow_up_questions: data.follow_up_questions,
					heading: data.heading,
				};
				if (data.id) {
					finalData.id = data.id;
				}
				await actionRequiredService.addFollowUp(finalData);
				toastSuccess('The changes have been saved successfully.');
				hideModalSendFollowUp();
				refetchDocuments();
			} catch (error) {
				toastError('An error occurred. Please try again.');
			}
		},
		[caseId, toastError, toastSuccess, hideModalSendFollowUp, refetchDocuments]
	);

	const retainerDocument = {
		id: undefined,
		title: 'Retainer',
		badges: ['Lawyer Signature', 'Preservation Date'],
		onClick: () => setIsModalRetainerOpen(true),
		isCompleted: false,
	};

	React.useEffect(() => {
		let retainer = { ...retainerDocument };
		let formattedDocuments: ItemDocument[] = [];

		if (lawyerSign?.data) {
			const isCompleted =
				!!lawyerSign.data.retain && !!lawyerSign.data.preservation_date;
			retainer = {
				...retainer,
				isCompleted: !!isCompleted,
			};
		}

		if (documents?.data) {
			formattedDocuments = documents.data.map((d) => {
				const isFollowUp = d.tags.some(
					(t) => t.type === 'YES_NO' || t.type === 'TEXT' || t.type === 'DATE'
				);
				const badges = isFollowUp
					? d.tags.map((t) => {
							if (t.type === 'YES_NO') return 'Yes/No';
							if (t.type) {
								return (
									t.type.charAt(0).toUpperCase() + t.type.slice(1).toLowerCase()
								);
							}
							return t.text;
					  })
					: d.tags
							.filter((t) => t.type !== 'DO_NOT_VALIDATE')
							.map((t) => t.text);
				const signatureBadges = d.tags
					.filter((t) => t.type === 'SIGNATURE')
					.map((t) => t.text);

				return {
					id: d.id,
					title: d.name,
					badges,
					signatureBadges,
					isCompleted: true,
					isFollowUp,
				};
			});
		}

		setListDocuments([retainer, ...formattedDocuments]);
	}, [lawyerSign, documents]);

	React.useEffect(() => {
		if (!documents?.data) return;
		setDocumentsPreviewData(
			documents.data.map((d) => ({
				name: d.name,
				email_text: d.email_text,
			}))
		);
	}, [documents, isModalDocumentOpen]);

	// Show save status
	React.useEffect(() => {
		setSaveStatus({ isSaving: isLoadingAutosaved });
	}, [isLoadingAutosaved, setSaveStatus]);

	const allDocumentsCompleted = listDocuments.every(
		(d) => d.isCompleted === true
	);

	const handleDelete = async (id: string | undefined) => {
		if (id) {
			deleteDocument(id);
		} else {
			deleteRetainer();
		}
	};

	const deleteDocument = async (id: string) => {
		try {
			await casesLawyerService.deleteDocument(caseId as string, id);
			refetchDocuments();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	const deleteRetainer = async () => {
		try {
			await casesLawyerService.saveLawyerSign('', '', caseId as string);
			refetch();
			if (caseId) {
				queryClient.refetchQueries({
					queryKey: [GET_CASE_STEPS_STATUS, caseId],
				});
			}
		} catch (error) {
			toastError('An error occurred while saving. Please try again.');
		}
	};

	const handleApprove = async (type: 'Retainer' | 'Document') => {
		setIsLoadingAutosaved(true);
		const isValid =
			type === 'Retainer'
				? true
				: lawyerSign?.data?.retain && lawyerSign?.data?.preservation_date;
		try {
			if (caseId && phaseId && stepId && statusStep !== 'approved' && isValid) {
				const data: ApprovePhase = {
					case_id: caseId,
					lawyer_stepper_id: phaseId,
					stepper_form_id: stepId,
				};
				await auditService.postApprovePhase(data);
				refetch();
				queryClient.refetchQueries({
					queryKey: [GET_CASE_STEPS_STATUS, caseId],
				});
				queryClient.refetchQueries({
					queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
				});
			}
		} catch (error) {
			toastError(
				'An error occurred while confirming approval. Please try again.'
			);
		} finally {
			setIsLoadingAutosaved(false);
		}
	};

	const handleRefetch = (data: CreateDocumentsToSend) => {
		setCurrentDocumentPreview(null);
		setDocumentsPreviewData((prev) => [...prev, data]);
		refetchDocuments();
	};

	const handlePReviewData = (data: CreateDocumentsToSend) => {
		setCurrentDocumentPreview(data);
	};

	const handleClose = () => {
		setDocumentsPreviewData([]);
		setCurrentDocumentPreview(null);
		setIsModalDocumentOpen(false);
	};

	const handleEditFollowUp = async (id?: string) => {
		if (!id) return;
		try {
			const followUpData = documents?.data.find((f) => f.id === id);
			if (followUpData && followUpData.data) {
				onOpenSendFollowUp({
					...followUpData.data,
					id: Number(id),
				});
			}
		} catch (error) {
			toastError('An error occurred while fetching the follow-up details.');
		}
	};

	return (
		<div className="text-left space-y-3">
			<div className="p-3 bg-[#2084E71A] flex items-center gap-x-3 rounded-lg xl:-ml-12">
				<Icon
					src={Icons.infoCircle}
					width={20}
					height={20}
					className="text-blue-link"
				/>
				<Typography type="regular-14" className="text-blue-link">
					Documents added here will be sent to the client as a single package
					for signature
				</Typography>
			</div>
			{listDocuments.map(
				(
					{
						id,
						title,
						badges,
						signatureBadges,
						isCompleted,
						onClick,
						isFollowUp,
					},
					index
				) => {
					return (
						<DocumentCard
							key={index}
							title={title}
							badges={badges}
							signatureBadges={signatureBadges}
							isCompleted={isCompleted ?? false}
							onClick={onClick}
							onDelete={() => handleDelete(id)}
							onEdit={isFollowUp ? () => handleEditFollowUp(id) : undefined}
							customIcon={isFollowUp ? Icons.messageQuestionEmpty : undefined}
						/>
					);
				}
			)}
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex gap-3">
					<Button
						iconLeft
						type="button"
						decoration="line-primary"
						icon={Icons.plus2}
						size="large"
						className="!h-12"
						label="Add Document"
						classNameButton="mr-3"
						onClick={() => setIsModalDocumentOpen(true)}
					/>
					<Button
						iconLeft
						type="button"
						decoration="line-primary"
						icon={Icons.plus2}
						size="large"
						className="!h-12"
						label="Add Follow-up"
						classNameButton="mr-3"
						onClick={() => onOpenSendFollowUp()}
					/>
				</div>

				<Button
					decoration="line-primary"
					type="button"
					label="Preview"
					size="large"
					className="!h-12"
					disabled={!allDocumentsCompleted}
					onClick={() => showPreviewPage()}
				/>
			</div>
			{isModalRetainerOpen && (
				<RetainerContent
					open={isModalRetainerOpen}
					setOpen={setIsModalRetainerOpen}
					isCaseReadOnly={false}
					caseId={caseId as string}
					handleApprove={handleApprove}
				/>
			)}
			{isModalDocumentOpen && (
				<DocumentContent
					open={isModalDocumentOpen}
					caseId={caseId as string}
					refetch={handleRefetch}
					handlePreviewData={handlePReviewData}
					handleClose={handleClose}
					handleApprove={handleApprove}
					documentsPreviewData={[
						...documentsPreviewData,
						currentDocumentPreview as CreateDocumentsToSend,
					]}
				/>
			)}
			<ModalPreviewPage
				handleClickBack={() => {
					hidePreviewPage();
				}}
				hideApproveButton
				hideEditButton={true}
				hideTitle={true}
				approveCustomLabel="Send Document"
			>
				<TemplatePreview documentsPreviewData={documentsPreviewData} />
			</ModalPreviewPage>

			{/* FollowUp Modal */}
			{renderSendFollowUpModals}
		</div>
	);
};
